import React from 'react';
import { cn } from '@/lib/utils';

/**
 * "Dash Route" logo for Ads Dashbound.
 *
 * Mark concept: a stylized letter D formed by a vertical stem + a curved
 * traffic route, with 3 glowing waypoint nodes (Source / Route / Destination)
 * and a subtle forward-motion chevron inside the D. The whole stroke uses
 * a cyan -> electric blue -> violet gradient with an outer glow halo.
 *
 * Variants:
 *  - "full"   : icon + wordmark, used in navbar + footer
 *  - "icon"   : icon-only (mobile, favicon, loading)
 *  - "footer" : icon + wordmark tuned for dark surfaces (slightly muted)
 */
export default function BrandLogo({
  variant = 'full',
  size = 36,
  withGlow = true,
  hoverPulse = true,
  className = '',
  testId = 'brand-logo',
}) {
  const showWord = variant !== 'icon';
  const wordCls = variant === 'footer'
    ? 'font-display text-[15px] font-bold tracking-[-0.02em] text-white'
    : 'font-display text-[16.5px] font-bold tracking-[-0.02em] text-white';

  return (
    <span
      data-testid={testId}
      className={cn('inline-flex items-center gap-2.5 group select-none', className)}
    >
      <DashRouteIcon size={size} withGlow={withGlow} hoverPulse={hoverPulse} />
      {showWord && (
        <span className={wordCls}>
          Ads{' '}
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: 'linear-gradient(90deg, #22D3EE, #3B82F6 50%, #8B5CF6)' }}
          >
            Dashbound
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * Standalone Dash Route icon (used by BrandLogo + favicon).
 * Pure SVG, scales sharply at any size.
 */
export function DashRouteIcon({ size = 36, withGlow = true, hoverPulse = true, ariaLabel }) {
  // Container is optional dark glass tile when used in navbar.
  const dimension = typeof size === 'number' ? `${size}px` : size;
  return (
    <span
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={!ariaLabel}
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: dimension, height: dimension }}
    >
      {withGlow && (
        <span
          className="absolute -inset-1 rounded-2xl blur-[10px] opacity-50 -z-10 transition-opacity group-hover:opacity-80"
          style={{
            background:
              'radial-gradient(circle, rgba(34,211,238,0.55), rgba(139,92,246,0.4) 60%, transparent 75%)',
          }}
          aria-hidden
        />
      )}
      <svg
        viewBox="0 0 64 64"
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
        className={hoverPulse ? 'overflow-visible' : 'overflow-visible'}
      >
        <defs>
          <linearGradient id="dr-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22D3EE" />
            <stop offset="50%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
          <linearGradient id="dr-tile" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0B172A" />
            <stop offset="100%" stopColor="#060B18" />
          </linearGradient>
          <radialGradient id="dr-tile-glow" cx="35%" cy="25%" r="80%">
            <stop offset="0%" stopColor="rgba(34,211,238,0.35)" />
            <stop offset="60%" stopColor="rgba(34,211,238,0)" />
          </radialGradient>
          <filter id="dr-soft" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="0.9" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Dark glass rounded tile */}
        <rect x="2" y="2" width="60" height="60" rx="14" fill="url(#dr-tile)" />
        <rect x="2" y="2" width="60" height="60" rx="14" fill="url(#dr-tile-glow)" />
        <rect x="2.5" y="2.5" width="59" height="59" rx="13.5" fill="none" stroke="rgba(125,220,255,0.22)" strokeWidth="1" />

        {/* "D" stem */}
        <line
          x1="18"
          y1="14"
          x2="18"
          y2="50"
          stroke="url(#dr-stroke)"
          strokeWidth="5"
          strokeLinecap="round"
          filter="url(#dr-soft)"
        />

        {/* "D" curved route bowl */}
        <path
          d="M 18 14 C 38 14, 52 22, 52 32 C 52 42, 38 50, 18 50"
          fill="none"
          stroke="url(#dr-stroke)"
          strokeWidth="5"
          strokeLinecap="round"
          filter="url(#dr-soft)"
        />

        {/* Forward-motion chevron (dash forward inside the D) */}
        <path
          d="M 26 27 L 34 32 L 26 37"
          fill="none"
          stroke="#FFFFFF"
          strokeOpacity="0.92"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={hoverPulse ? 'transition-transform duration-300 ease-out origin-center group-hover:translate-x-[1.2px]' : ''}
        />

        {/* Glowing waypoint nodes: Source, Route, Destination */}
        {/* Source (top-left junction) */}
        <g className={hoverPulse ? 'logo-node logo-node-1' : ''}>
          <circle cx="18" cy="14" r="3.4" fill="#22D3EE" />
          <circle cx="18" cy="14" r="6" fill="#22D3EE" opacity="0.22" />
        </g>
        {/* Route apex (right-middle of curve) */}
        <g className={hoverPulse ? 'logo-node logo-node-2' : ''}>
          <circle cx="52" cy="32" r="3.4" fill="#3B82F6" />
          <circle cx="52" cy="32" r="6" fill="#3B82F6" opacity="0.22" />
        </g>
        {/* Destination (bottom-left junction) */}
        <g className={hoverPulse ? 'logo-node logo-node-3' : ''}>
          <circle cx="18" cy="50" r="3.4" fill="#8B5CF6" />
          <circle cx="18" cy="50" r="6" fill="#8B5CF6" opacity="0.22" />
        </g>
      </svg>
    </span>
  );
}
